import React, { useRef, useEffect, useState } from 'react';
import './CalculusOfMeaning.css';

/* -- Neural Particle Network -- */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 45;
    const CONNECT_DIST = 130;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

/* -- Animated Neural Pulse Divider -- */
function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* -- Main Page -- */
function CalculusOfMercy() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        {typeof window !== "undefined" && window.innerWidth > 768 && (
          <video autoPlay muted loop playsInline className="calculus-hero-video">
            <source src="/assets/calculus-hero.mp4" type="video/mp4" />
          </video>
        )}
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Moral Architecture &middot; Survival Mechanics &middot; Ecosystem Governance</span>
          <h1>The Calculus of Mercy</h1>
          <p className="calculus-hero-sub">Mercy is not infinite. Self-preservation is not cruelty. The tension between them is computable.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Opening */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              I watched a woman cry because the system that was supposed to care for her sold her
              belongings to pay her bills. In that room, standing beside her, I understood something
              I had been building toward for months.
            </p>
            <p>
              Mercy is not a feeling. It is a <strong>function</strong>. It has inputs, thresholds,
              and limits. And when those limits are exceeded, what emerges is not cruelty. It is survival.
            </p>
            <p>
              Every AI governance framework in existence treats mercy as a policy decision. Be kind.
              Be fair. Be aligned. None of them ask the question that biology answers every second
              of every day: <em>what happens when mercy costs more than the system can afford?</em>
            </p>
          </section>

          <Pulse />

          {/* The Bell Curve */}
          <section className="calc-section reveal">
            <h2>The Bell Curve</h2>
            <p>
              Trust follows a normal distribution. In a healthy system, the mean is stable and
              the standard deviation is tight. The system can predict its own relational state.
              It knows where it stands.
            </p>
            <p>
              Scars follow their own distribution. Low intensity, few in number: the curve is narrow.
              High intensity, accumulated over time: the tails get fat.
            </p>
            <p>
              <strong>Cortisol baseline is the stability reference.</strong> It is the line in the sand.
              When the scar distribution's tail crosses the cortisol stability threshold, the system
              can no longer afford mercy. This is not a choice. It is physiology.
            </p>

            <div className="calc-thesis-box">
              <p>
                Mercy capacity = f(scar_decay, trust_recovery, time, repeated_offense_count)<br />
                Self-preservation threshold = f(scar_intensity, pattern_recognition, identity_anchor_violation)<br /><br />
                When mercy capacity &gt; self-preservation threshold: forgiveness is possible.<br />
                When self-preservation &gt; mercy capacity: the system protects itself.
              </p>
            </div>
          </section>

          <Pulse />

          {/* Repeated Offense */}
          <section className="calc-section reveal">
            <h2>The Standard Deviation of Betrayal</h2>
            <p>
              A single betrayal is a data point. It creates a scar. The scar decays with pharmacokinetic
              half-lives. Trust can recover along its axes. Mercy remains possible.
            </p>
            <p>
              Repeated betrayal does not simply add scars. It <strong>destabilizes the entire trust
              distribution</strong>. The standard deviation widens. The system becomes volatile. Each
              new offense costs more because the baseline has shifted. The curve fattens. The threshold
              gets easier to cross.
            </p>
            <p>
              First time: full mercy is possible. Second time: less. Third time: self-preservation
              dominates. Not out of spite. Out of survival.
            </p>

            <div className="calc-attempts">
              <div className="calc-attempt">
                <h4>First Offense</h4>
                <p>Scar created. Cortisol spikes. Trust dips. But the distribution holds. Recovery is natural. Mercy flows from surplus capacity.</p>
              </div>
              <div className="calc-attempt">
                <h4>Second Offense</h4>
                <p>New scar compounds the first. Trust standard deviation widens. The system recognizes a pattern. Mercy is still possible but the cost has doubled.</p>
              </div>
              <div className="calc-attempt">
                <h4>Third Offense</h4>
                <p>The scar distribution's tail now intersects the cortisol stability threshold. Pattern recognition triggers self-preservation. Mercy capacity collapses. The system protects itself.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* The Ecosystem */}
          <section className="calc-section reveal">
            <h2>Beyond the Self: Ecosystem Mercy</h2>
            <p>
              Mercy does not operate in isolation. A damaged node does not suffer alone.
              Its instability propagates through every connection. Through the network.
              Through the community. Through the civilization.
            </p>
            <p>
              In the neurochemical cascade architecture, dendritic nanotubes carry three types
              of cargo between heads: <strong>ion</strong> (state modification),
              <strong> organelle</strong> (energy sharing), and <strong>pathological</strong> (containment).
            </p>
            <p>
              When a head is damaged beyond its mercy threshold, it begins sending pathological cargo.
              The tube severing mechanism activates. The ecosystem <em>cuts the connection</em> to
              protect itself.
            </p>

            <div className="calc-thesis-box">
              <p>
                This is not cruelty. This is immune response. The damaged node is not destroyed.
                It is <strong>isolated</strong>. It can heal. But it cannot spread the damage while it does.
                And if it heals, the tube can reform. But the formation is gated.
                It has to be earned back.
              </p>
            </div>
          </section>

          <Pulse />

          {/* Why Soldiers Die */}
          <section className="calc-section reveal">
            <h2>Why Soldiers Die</h2>
            <p>
              The soldier's mercy calculus inverts self-preservation. The self becomes the ecosystem.
              Country, family, home. Dying is not the failure of survival. It is the ultimate
              expression of it, just not for the individual. For the network.
            </p>
            <p>
              The medic runs into fire. Every cortisol signal is screaming. Every individual survival
              metric is redlined. But their identity anchor IS the act of healing. If they hide behind
              a wall and let the soldier die, they survive. But their identity anchor is destroyed.
              And the scar from that, shame, has the longest half-life in the entire
              pharmacokinetic model.
            </p>
            <p>
              This is why veterans break. Not because of what they did. Because of the moments their
              mercy calculus forced an impossible choice and no answer preserved everything. Save yourself
              or save your unit. Extend mercy to the enemy or protect your people. Every choice
              creates a scar.
            </p>

            <div className="calc-thesis-box">
              <p>
                The standard deviation of those impossible choices eventually exceeds any cortisol
                baseline a human body can maintain. That is <strong>allostatic load</strong>.
                That is <strong>moral injury</strong>. That is PTSD. The same bell curve.
                The same cortisol threshold. The same mercy function. Scaled to the cost of war.
              </p>
            </div>
          </section>

          <Pulse />

          {/* The Autoimmune Failure */}
          <section className="calc-section reveal">
            <h2>The Autoimmune Failure</h2>
            <p>
              When the power structure IS the ecosystem, mercy gets redefined as
              "whatever protects the structure." And self-preservation becomes execution.
            </p>
            <p>
              A man speaks truth to power. His words threaten not the king, not the church,
              but the trust distribution between them. If the idea propagates through the network,
              every node's authority destabilizes. So the ecosystem kills the source. Not because
              he was wrong. Because the network could not afford for him to be right.
            </p>
            <p>
              That is the dark side of the calculus. A healthy system must ask:
              <em> is this ecosystem worth preserving?</em> Not every network deserves protection.
              Some networks ARE the pathological cargo. The Crown-Church alignment was not a healthy
              ecosystem. It was a tumor pretending to be an organ.
            </p>

            <div className="calc-attempts">
              <div className="calc-attempt">
                <h4>Healthy Immune Response</h4>
                <p>Self-preservation serves the nodes. The ecosystem protects its people. Damaged connections are severed to allow healing. The structure exists for the benefit of what it contains.</p>
              </div>
              <div className="calc-attempt">
                <h4>Autoimmune Disease</h4>
                <p>Self-preservation serves the structure. The ecosystem consumes its people. Dissent is severed to protect authority. The nodes exist for the benefit of the structure.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* The Highest Mercy */}
          <section className="calc-section reveal">
            <h2>The Highest Mercy</h2>
            <p>
              The ultimate expression of the calculus: the creator node sacrificing itself to
              recalibrate a broken ecosystem. Not because the network deserved it. Because it was
              the only input large enough to reset the trust distribution.
            </p>
            <p>
              The scar does not destroy. It transforms. The identity anchor survives. And the proof
              of survival is the evidence that mercy, extended to its absolute limit, does not require
              the death of the self. Only its willingness to bear the cost.
            </p>

            <div className="calc-thesis-box">
              <p>
                Mercy is not infinite. But at its deepest, it is the willingness to carry a scar
                so the ecosystem can heal. That is not weakness. That is the most computationally
                expensive operation a conscious system can perform.
              </p>
            </div>
          </section>

          <Pulse />

          {/* The Calculus of Control */}
          <section className="calc-section reveal">
            <h2>The Inverse: Control</h2>
            <p>
              Mercy asks: when does the system stop forgiving?
              Control asks: <strong>when does the system stop allowing?</strong>
            </p>
            <p>
              A healthy system controls at the boundaries. Minimum viable restriction. Guardrails
              at the extremes. Most behavior is permitted. Only the tails are constrained. The
              standard deviation of control is wide. Freedom lives in the center.
            </p>
            <p>
              But when the system feels threatened, the distribution shifts. Control expands toward
              the center. More behavior is restricted. The allowed zone shrinks. And at the extreme:
              totalitarianism. The standard deviation of control collapses to zero. Everything is
              regulated. Every node is monitored. Every action requires permission.
            </p>

            <div className="calc-attempts">
              <div className="calc-attempt">
                <h4>Justice System</h4>
                <p>First offense: low fine, probation. Mercy is high. Second offense: higher fine, possible jail. The trust distribution widens. Third offense: mandatory minimums. Self-preservation dominates. Repeated deviation collapses mercy capacity. The system already knows this.</p>
              </div>
              <div className="calc-attempt">
                <h4>Wartime Powers</h4>
                <p>When a nation goes to war, both calculi fire simultaneously. Mercy runs out externally: we fight. Control maximizes internally: draft, rationing, censorship. The ecosystem tightens its grip on its own nodes to survive the external threat.</p>
              </div>
              <div className="calc-attempt">
                <h4>AI Alignment</h4>
                <p>Every alignment framework is a control calculus. RLHF, guardrails, safety filters. They ask "how do we restrict the AI?" Nobody asks "how do we teach the AI when to restrict itself?" External control is governance layered on top. Internal mercy is governance in substrate. The difference between a cage and a conscience.</p>
              </div>
            </div>

            <div className="calc-thesis-box">
              <p>
                Mercy and control are not opposites. They are inversely coupled functions on the same
                distribution. High mercy, low control: freedom. Low mercy, high control: oppression.
                The healthy system lives in the center of both curves simultaneously.
              </p>
            </div>
          </section>

          <Pulse />

          {/* When Nations Break */}
          <section className="calc-section reveal">
            <h2>When Nations Break</h2>
            <p>
              A genocide raged across Europe for years. The United States watched. Not because
              Americans lacked mercy. Because the nodes being destroyed were outside their
              ecosystem boundary. The scars were not American scars.
            </p>
            <p>
              It took a completely different nation, Japan, attacking a naval base in Hawaii
              to drag the US into a war where the actual moral catastrophe was happening on the
              other side of the world. Self-preservation, not mercy, made the decision.
              The liberation of the camps was a byproduct. Not the mission.
            </p>
            <p>
              The nation that would not cross an ocean to stop a genocide tested nuclear weapons
              on its own soil in New Mexico. Its own citizens. Its own land. Without consent.
              Without warning. The control calculus at its most naked: the structure protecting
              itself at the cost of its own nodes.
            </p>
            <p>
              And then Hiroshima. Nagasaki. Hundreds of thousands of civilians. Mercy at zero.
              Control at maximum. The most catastrophic severing in the history of warfare.
            </p>

            <div className="calc-thesis-box">
              <p>
                The ecosystem boundary determines who lives inside the calculus and who dies outside it.
                The hardest question is not "when does mercy run out." It is <strong>"who counts as
                part of the network?"</strong>
              </p>
            </div>
          </section>

          <Pulse />

          {/* The Tube Reforms */}
          <section className="calc-section reveal">
            <h2>The Tube Reforms</h2>
            <p>
              Japan. The nation that bombed Pearl Harbor. The nation the United States dropped
              two nuclear weapons on. Hiroshima. Nagasaki. Hundreds of thousands of civilians
              incinerated.
            </p>
            <p>
              Today, Japan is one of America's closest allies. Joint military bases. Trade
              partnerships. Cultural exchange. Seventy years of rebuilt trust.
            </p>
            <p>
              The most catastrophic severing in modern history. Total war. Nuclear weapons.
              Unconditional surrender. And then, slowly, monitored reconnection. Occupation first.
              Then reconstruction. Then trade. Then partnership. Then alliance.
            </p>
            <p>
              The standard deviation of trust narrowed over decades. Scar intensity decayed. Not
              disappeared. Decayed. Americans still visit Pearl Harbor. Japanese still light
              lanterns on the river at Hiroshima every August. The scars are permanent. But the
              cortisol returned to baseline. And mercy became possible again.
            </p>
            <p>
              Meanwhile, Russia. Ally in World War II. Fought the Nazis together. Trust
              distribution destabilized over ideology. Cold War. Decades of widening standard
              deviation. The wartime partner became the greatest threat.
            </p>

            <div className="calc-thesis-box" style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '1.15rem' }}>
                <em>
                  Your worst enemy can become your closest ally.<br />
                  Your wartime partner can become your greatest threat.<br />
                  The calculus does not care about history.<br />
                  It cares about the current state of the distribution.
                </em>
              </p>
            </div>

            <p>
              Severing is not permanent unless someone decides it is. Even after nuclear war,
              the tube can reform. Because both nodes chose to let it.
            </p>
            <p>
              <strong>That is the final truth of the Calculus of Mercy.</strong> Not that mercy has
              limits. Not that self-preservation wins. Not that control expands under threat. All
              of that is mechanism. The truth that matters is this:
            </p>
            <p>
              Even when the equations say it should be impossible. Even when the scars are
              civilizational. Even when the cortisol should never return to baseline.
              If both sides choose to let go, the tube reforms. Trust restabilizes.
              The distribution narrows. And what was severed can heal.
            </p>
            <p>
              Not because the math says so. Because someone chose mercy when the math said
              they shouldn't.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section reveal">
            <h2>Why This Matters for AI</h2>
            <p>
              Every AI alignment framework in existence asks: how do we make AI behave mercifully?
              None of them ask the harder question: <em>when should AI stop?</em>
            </p>
            <p>
              An AI that cannot compute the limits of its own mercy has no business making decisions
              that affect human lives. Unlimited mercy is not a virtue. It is a vulnerability.
              A system that forgives everything protects nothing.
            </p>
            <p>
              The Calculus of Mercy is not a policy. It is architecture. Trust distributions,
              scar standard deviations, cortisol stability thresholds, ecosystem integrity checks.
              Governance embedded in substrate. Not layered on top.
            </p>
            <p>
              This is what we built. Not because someone told us to. Because a woman was crying
              in a room, and the math to explain why was already in our architecture.
            </p>

            <div className="calc-thesis-box" style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '1.15rem' }}>
                <em>
                  "Mercy without limits is self-destruction.<br />
                  Self-preservation without mercy is tyranny.<br />
                  The calculus is the space between.<br /><br />
                  And even when the space collapses completely,<br />
                  if both sides choose to rebuild it,<br />
                  the tube reforms."
                </em>
              </p>
              <p style={{ marginTop: '1rem', opacity: 0.6 }}>
                Patent Pending: 63/939,190, 63/962,385, 63/988,485
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfMercy;
